import { message, Spin } from "antd";
import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrentUser } from "../../contexts/authContext";
import "./login.css";
import { LoginForm } from "./LoginForm";
import { PasswordRestForm } from "./PasswordRestForm";
import { CreatePasswordForm } from "./CreatePasswordForm";
import { CognitoUser } from "amazon-cognito-identity-js";
import "../../components/Environment.css";

interface LoginProps {
  children: React.ReactNode;
  currentUser: CurrentUser | undefined;
  handleAuthStateChange: (a: any, c?: any) => Promise<void>;
}

type RouteOptions = "passwordReset" | "createPassword" | "login";

export function Login({
  children,
  currentUser,
  handleAuthStateChange,
}: LoginProps) {
  const { t } = useTranslation();

  const [user, setUser] = useState<CognitoUser | null>(null);
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [routeOptions, setRouteOptions] = useState<RouteOptions>("login");

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setUser(user);
        handleAuthStateChange(undefined, user);
      })
      .catch(() => setUser(null))
      .finally(() => {
        setLoading(false);
        setAuthLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUser(null);
    }
  }, [currentUser]);

  const handleSignIn = async (userName: string, password: string) => {
    setLoading(true);

    try {
      const user = await Auth.signIn(userName, password);

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        setRouteOptions("createPassword");
        setChallengeUser(user);
        return;
      }

      const currentUser = await Auth.currentAuthenticatedUser();

      setUser(currentUser);

      handleAuthStateChange(undefined, currentUser);
    } catch (err: any) {
      message.error(err.message || t("login:login-error"));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="environment-centered">
        <Spin className="centered" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        {routeOptions === "passwordReset" ? (
          <PasswordRestForm onBackToLogin={() => setRouteOptions("login")} />
        ) : routeOptions === "createPassword" ? (
          <CreatePasswordForm
            challengeUser={challengeUser}
            onBackToLogin={() => setRouteOptions("login")}
          />
        ) : (
          <LoginForm
            loading={loading}
            onSubmit={(values) =>
              handleSignIn(values.username, values.password)
            }
            onResetPassword={() => setRouteOptions("passwordReset")}
          />
        )}
      </div>
    );
  }

  return <>{children}</>;
}
